import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {
  useEnterGroupChatMutation,
  useGroupChatCheckMemberQuery,
} from '~/features/chatRoom';
import { EnterGroupChatRequest } from '~/types';
import { useCommonChatStore, useGroupChatStore, useUserStore } from '~/store';
import { useGroupChatErrorHandler } from '..';

/**
 * 그룹채팅 입장 flow
 * 1. 그룹채팅방에 참여한 인원인지 확인
 *    - 그룹 채팅방에 참여한 인원이라면 4번으로 가기
 *
 * 2. 그룹채팅방에 참여하지 않은 인원이라면, 비밀번호 입력란 띄우기
 * 3. 비밀번호 확인하면서, 방에 참여할 수 있는지 확인
 *
 * 4.이전 대화내역 불러온 후 대화 시작
 */

const useAuthGroupChat = () => {
  const email = useUserStore((state) => state.email);
  const { id: chatroomId } = useParams();

  const { mutate: enterGroupChatMutate, isPending: isEnterChatPending } =
    useEnterGroupChatMutation();
  const { data: checkMemberResponse, isPending: isCheckingMember } =
    useGroupChatCheckMemberQuery({
      chatroom_id: chatroomId || '',
    });

  const resetAll = useCommonChatStore((state) => state.resetAll);
  const { step, setStep, resetStep } = useGroupChatStore((state) => ({
    step: state.enterGroupChatStep,
    setStep: state.setEnterGroupChatStep,
    resetStep: state.resetAll,
  }));

  const [passwordTitle, setPasswordTitle] = useState('');
  const [passwordThumbnails, setPasswordThumbnails] = useState<string[]>([]);
  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [isWaitingChatRoomInfo, setIsWaitingChatRoomInfo] = useState(false);

  const { handleChatError } = useGroupChatErrorHandler();

  useEffect(() => {
    if (!email || !chatroomId || !checkMemberResponse) return;
    setIsWaitingChatRoomInfo(true);

    try {
      if (checkMemberResponse.status === 200) {
        const { is_member, accessibility, title, ai_thumbnails } =
          checkMemberResponse.data;
        if (is_member) {
          setIsWaitingChatRoomInfo(false);
          setStep('Chat');
        } else {
          if (accessibility === 'private') {
            if (title && ai_thumbnails) {
              setPasswordTitle(title);
              setPasswordThumbnails(ai_thumbnails);
            }
            setStep('Password');
          } else {
            const requestData: EnterGroupChatRequest = {
              chatroom_id: chatroomId,
              accessibility: 'public',
            };

            enterGroupChatMutate(requestData, {
              onSuccess: (response) => {
                setIsWaitingChatRoomInfo(false);
                setStep('Chat');
              },
            });
          }
        }
      }
    } catch (error: any) {
      console.error(error);
      handleChatError(error.message);
    } finally {
      setIsWaitingChatRoomInfo(false);
    }

    return () => {
      resetStep();
      resetAll();
    };
  }, [chatroomId, email, checkMemberResponse]);

  const handlePasswordClick = () => {
    if (!chatroomId || !password) return;
    if (isEnterChatPending) return;

    setPasswordError('');

    const requestData: EnterGroupChatRequest = {
      chatroom_id: chatroomId,
      accessibility: 'private',
      password: password,
    };

    enterGroupChatMutate(requestData, {
      onSuccess: (response) => {
        setIsWaitingChatRoomInfo(false);
        setStep('Chat');
      },
    });
  };

  return {
    step,
    password,
    setPassword,
    passwordTitle,
    passwordError,
    setPasswordError,
    passwordThumbnails,
    successEnter: !isWaitingChatRoomInfo && step === 'Chat',
    isLoading: isWaitingChatRoomInfo || isCheckingMember || isEnterChatPending,
    handlePasswordClick,
  };
};

export default useAuthGroupChat;
